import { createSelector } from "reselect";

import { NEW_ACCOUNT_VALUE } from "app/pages/sales-intelligence/domain/crm-integration/constants/account";
import { selectDynamicsAccountsToggleState } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import { selectHubspotAccountsToggleState } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import { selectSalesforceAccountsToggleState } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { createStatePropertySelector } from "app/pages/sales-intelligence/store/utils";

const select = createStatePropertySelector(selectSalesIntelligenceState("crmExport"));

export const selectAccountsExportPhase = select("phase");
export const selectAccountsPreview = select("preview");
export const selectAccountsIntegration = select("integration");
export const selectAccountsLeadsType = select("leadsType");
export const selectAccountsExportCountries = select("countries");
export const selectAccountsExportListId = select("listId");

export const selectAccountsCrmName = createSelector(selectAccountsIntegration, (crm) => (crm ? getCrmName(crm) : ""));

export const selectIsAccountsExportInProgress = createSelector(
    selectAccountsExportPhase,
    (phase) => phase.type === "matchingAccounts" || phase.type === "exportingAccounts",
);

export const selectAccountsExportCount = createSelector(selectAccountsPreview, (preview) => preview.length);

export const selectMatchedAccountsExportCount = createSelector(
    selectAccountsPreview,
    (preview) => preview.filter((item) => item.matchedCompanies.length > 0).length,
);

export const selectAccountsExportMapping = createSelector(selectAccountsPreview, (preview) =>
    preview.map((item) => {
        const isNewAccount = !item.accountId || item.accountId === NEW_ACCOUNT_VALUE;

        // Existing account -> its id takes priority, no custom name needed.
        if (!isNewAccount) {
            return { domain: item.domain, accountId: item.accountId as string };
        }

        // New account -> send the user-edited name when present; otherwise let the backend default it.
        const customName = item.newAccountName?.trim();

        return customName
            ? { domain: item.domain, accountId: "", companyName: customName }
            : { domain: item.domain, accountId: "" };
    }),
);


export const selectCanCreateNewAccountForExport = createSelector(
    selectAccountsIntegration,
    selectSalesforceAccountsToggleState,
    selectHubspotAccountsToggleState,
    selectDynamicsAccountsToggleState,
    (crm, canCreateSalesforceAccount, canCreateHubspotAccount, canCreateDynamicsAccount) => {
        if (crm === "salesforce") {return canCreateSalesforceAccount;}
        if (crm === "dynamics") {return canCreateDynamicsAccount;}
        return canCreateHubspotAccount;
    },
);

/**
 * Export is blocked when the user is not allowed to create new CRM accounts, yet at least one row
 * still resolves to "create new" (no match, or explicitly picked). Data selection stays in the
 * store; only the permission gating is derived here.
 */
export const selectIsAccountsExportBlocked = createSelector(
    selectAccountsPreview,
    selectCanCreateNewAccountForExport,
    (preview, canCreateNewAccount) =>
        !canCreateNewAccount && preview.some((item) => item.accountId === NEW_ACCOUNT_VALUE),
);
